import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function VancouverBikeGuide() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "buy-bike-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Where to Buy a Bike in Vancouver",
    "section-2": "New vs. Used: Pros and Cons",
    "section-3": "Best Practices for Buying a Second-Hand Bike",
    "section-4": "Local Regulations and Best Practices",
    "section-5": "Top Bike-Friendly Routes and Trails",
    faq: "FAQ",
    "local-regulations": "Local Regulations",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://vancouver.ca/streets-transportation/cycling.aspx",
      label: "City of Vancouver – Cycling Info",
    },
    {
      url: "https://bikehub.ca/",
      label: "HUB Cycling (Metro Vancouver)",
    },
    {
      url: "https://project529.com/garage",
      label: "Project 529 Garage – Bike Registry",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // The conversation includes coordinates for:
  // - MEC Vancouver: (49.26950, -123.10247)
  // - West Point Cycles: (~49.26930, -123.09900)
  // - Landyachtz: (49.27678, -123.08050)
  // - Denman Bike Shop (Main St): (49.26100, -123.10150)
  // - Veloholic Cycles: (49.23000, -123.09000)
  // - Our Community Bikes: (49.26297, -123.10106)
  // - The Bike Kitchen (UBC): (49.26650, -123.24930)
  // - Sports Junkies: (49.26320, -123.10890)
  // - Bikes For All: (49.26460, -123.09950)
  // - Kickstand: (49.27670, -123.08300)
  // - Starting points for rides: 
  //   - Stanley Park Coal Harbour entrance: (49.29720, -123.13000)
  //   - Science World: (49.27345, -123.10350)
  //   - New Westminster Quay: (49.20197, -122.91254)
  //   - Arbutus Greenway start: (49.26800, -123.13900)
  //   - Arbutus Greenway end: (49.20950, -123.14000)
  //   - English Bay: (49.28530, -123.14285)
  //   - Jericho Beach Park: (49.27440, -123.19334)
  //   - UBC MOA: (49.26980, -123.25900)

  const locations = [
    // Where to Buy a Bike in Vancouver
    {
      id: "mec-vancouver",
      lat: 49.26950,
      lng: -123.10247,
      popupText: "MEC Vancouver",
    },
    {
      id: "westpoint-main",
      lat: 49.26930,
      lng: -123.09900,
      popupText: "West Point Cycles (Main Street)",
    },
    {
      id: "landyachtz",
      lat: 49.27678,
      lng: -123.08050,
      popupText: "Landyachtz Factory Store",
    },
    {
      id: "denman-main",
      lat: 49.26100,
      lng: -123.10150,
      popupText: "Denman Bike Shop (Main St)",
    },
    {
      id: "veloholic",
      lat: 49.23000,
      lng: -123.09000,
      popupText: "Veloholic Cycles",
    },
    {
      id: "ocb",
      lat: 49.26297,
      lng: -123.10106,
      popupText: "Our Community Bikes",
    },
    {
      id: "bike-kitchen",
      lat: 49.26650,
      lng: -123.24930,
      popupText: "The Bike Kitchen (UBC)",
    },
    {
      id: "sports-junkies",
      lat: 49.26320,
      lng: -123.10890,
      popupText: "Sports Junkies",
    },
    {
      id: "bikes-for-all",
      lat: 49.26460,
      lng: -123.09950,
      popupText: "Bikes For All",
    },
    {
      id: "kickstand",
      lat: 49.27670,
      lng: -123.08300,
      popupText: "Kickstand (East Van Community Bike Shop)",
    },

    // Top Bike-Friendly Routes
    {
      id: "stanley-park",
      lat: 49.29720,
      lng: -123.13000,
      popupText: "Stanley Park Seawall Loop Start",
    },
    {
      id: "science-world",
      lat: 49.27345,
      lng: -123.10350,
      popupText: "Science World (False Creek)",
    },
    {
      id: "new-west-quay",
      lat: 49.20197,
      lng: -122.91254,
      popupText: "New Westminster Quay",
    },
    {
      id: "arbutus-start",
      lat: 49.26800,
      lng: -123.13900,
      popupText: "Arbutus Greenway (5th & Fir)",
    },
    {
      id: "arbutus-end",
      lat: 49.20950,
      lng: -123.14000,
      popupText: "Arbutus Greenway (Milton St & W 75th Ave)",
    },
    {
      id: "english-bay",
      lat: 49.28530,
      lng: -123.14285,
      popupText: "English Bay",
    },
    {
      id: "jericho-beach",
      lat: 49.27440,
      lng: -123.19334,
      popupText: "Jericho Beach Park",
    },
    {
      id: "ubc-moa",
      lat: 49.26980,
      lng: -123.25900,
      popupText: "UBC (MOA)",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Ultimate Guide: Buying a New or Used Bike in Vancouver</title>
        <meta
          name="description"
          content="Learn where to buy new or used bikes in Vancouver, tips for second-hand purchases, local regulations, top cycling routes, and more."
        />
        <meta
          name="keywords"
          content="vancouver bikes, buy a bike Vancouver, used bikes, cycling Vancouver"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/buy-bike-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Ultimate Guide: Buying a New or Used Bike in Vancouver"
        />
        <meta
          property="og:description"
          content="Discover the best bike shops, used bike tips, local regulations, and top cycling routes in Vancouver."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/buy-bike-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/buy-bike-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Ultimate Guide: Buying a New or Used Bike in Vancouver"
        />
        <meta
          name="twitter:description"
          content="Where to buy, how to inspect used bikes, local laws, and the best routes in Vancouver."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/buy-bike-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="Cyclists riding along Vancouver's waterfront"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver is a cyclist’s paradise, with scenic waterfront routes,
              well-marked bike lanes, and a thriving cycling community. Whether
              you’re looking to cut transportation costs, stay active, or simply
              enjoy the city’s gorgeous views, owning a bike here is a fantastic
              choice. This guide will help you find the perfect bike—new or
              used—and share essential tips on local regulations, second-hand
              buying strategies, and the best trails in town.
            </p>

            {/* SECTION: Where to Buy a Bike in Vancouver */}
            <h2 id="section-1">Where to Buy a Bike in Vancouver</h2>
            <p>
              Vancouver is home to numerous bike shops, co-ops, and community
              programs. Here are some reputable places to get a bike, whether
              you want it brand new or gently used:
            </p>

            <h3>New Bike Shops</h3>
            <ul>
              <li
                ref={(el) => setSpotRef("mec-vancouver", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>MEC Vancouver</strong>{" "}
                <Marker
                  coordY={49.26950}
                  coordX={-123.10247}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />{" "}
                – Offers a wide range of new commuter, road, and mountain bikes
                at <em>111 East 2nd Ave</em>. Expect prices from ~$500 and up.
                They have an in-store repair shop and helpful staff.
              </li>
              <li
                ref={(el) => setSpotRef("westpoint-main", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>West Point Cycles (Main Street)</strong>{" "}
                <Marker
                  coordY={49.26930}
                  coordX={-123.09900}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                – Located at <em>215 East 2nd Ave</em>, this historic shop
                specializes in quality brands like Trek and offers a good range
                of road, hybrid, and e-bikes.
              </li>
              <li
                ref={(el) => setSpotRef("landyachtz", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Landyachtz Factory Store</strong>{" "}
                <Marker
                  coordY={49.27678}
                  coordX={-123.08050}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                – A unique shop at <em>1146 Union St</em> specializing in
                Landyachtz commuter bikes and longboards. Great for gravel and
                urban riders.
              </li>
              <li
                ref={(el) => setSpotRef("denman-main", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Denman Bike Shop (Main St.)</strong>{" "}
                <Marker
                  coordY={49.26100}
                  coordX={-123.10150}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                – Found at <em>2607 Main Street</em>, carrying a variety of city
                hybrids and e-bikes, plus on-site repairs.
              </li>
              <li
                ref={(el) => setSpotRef("veloholic", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Veloholic Cycles</strong>{" "}
                <Marker
                  coordY={49.23000}
                  coordX={-123.09000}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                – Located at <em>5850 Fraser Street</em> for high-performance
                road, gravel, and custom builds.
              </li>
            </ul>

            <h3>Second-Hand Bike Shops and Co-ops</h3>
            <ul>
              <li
                ref={(el) => setSpotRef("ocb", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Our Community Bikes (OCB)</strong>{" "}
                <Marker
                  coordY={49.26297}
                  coordX={-123.10106}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                – A non-profit at <em>2429 Main Street</em>, offering refurbished
                bikes and DIY repair assistance. Great for budget-friendly
                finds.
              </li>
              <li
                ref={(el) => setSpotRef("bike-kitchen", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>The Bike Kitchen (UBC)</strong>{" "}
                <Marker
                  coordY={49.26650}
                  coordX={-123.24930}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                – Operated by UBC’s AMS Bike Co-op at <em>6138 Student Union
                Blvd</em>. Ideal for students seeking affordable used bikes.
              </li>
              <li
                ref={(el) => setSpotRef("sports-junkies", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Sports Junkies</strong>{" "}
                <Marker
                  coordY={49.26320}
                  coordX={-123.10890}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                – A large store at <em>102 West Broadway</em> buying and selling
                used sporting goods, including bikes with basic warranties.
              </li>
              <li
                ref={(el) => setSpotRef("bikes-for-all", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Bikes For All</strong>{" "}
                <Marker
                  coordY={49.26460}
                  coordX={-123.09950}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                – A shop at <em>112 East 7th Ave</em> that sells both new and
                used bikes, often at very budget-friendly prices.
              </li>
              <li
                ref={(el) => setSpotRef("kickstand", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Kickstand (East Van Community Bike Shop)</strong>{" "}
                <Marker
                  coordY={49.27670}
                  coordX={-123.08300}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                – Located at <em>1187 Parker Street</em> (rear entrance),
                volunteer-run with extremely affordable refurbished bikes and DIY
                repair.
              </li>
            </ul>

            {/* SECTION: New vs. Used: Pros and Cons */}
            <h2 id="section-2">New vs. Used: Pros and Cons</h2>
            <p>
              Deciding between a brand-new bike and a second-hand one often
              comes down to budget, warranty considerations, and personal
              preference.
            </p>
            <ul>
              <li>
                <strong>New Bikes – Pros:</strong> Manufacturer warranties, free
                tune-ups, modern components, and peace of mind with no hidden
                history.{" "}
              </li>
              <li>
                <strong>New Bikes – Cons:</strong> Higher price tag and quick
                depreciation.
              </li>
              <li>
                <strong>Used Bikes – Pros:</strong> Significant cost savings,
                sustainability, potential for higher-end models at lower prices.
              </li>
              <li>
                <strong>Used Bikes – Cons:</strong> No warranty, uncertain
                wear-and-tear, risk of buying stolen bikes.
              </li>
            </ul>

            {/* SECTION: Best Practices for Buying a Second-Hand Bike */}
            <h2 id="section-3">Best Practices for Buying a Second-Hand Bike</h2>
            <p>
              If you’re leaning toward a pre-loved bike, here’s how to ensure
              you get a great deal without inheriting major problems.
            </p>
            <h4>1. Inspect Thoroughly</h4>
            <p>
              Look for frame damage, check brakes, tires, drivetrain, and take
              it for a quick test ride if possible. A small investment in a shop
              inspection can save big repair costs later.
            </p>
            <h4>2. Reputable Sources</h4>
            <p>
              Used bike shops and co-ops typically tune bikes before selling.
              Online marketplaces like Craigslist or Facebook Marketplace can
              work if you carefully verify the bike’s condition and history.
            </p>
            <h4>3. Avoid Stolen Bikes</h4>
            <p>
              Ask for the serial number and check it on <strong>Project 529</strong> or
              <strong> BikeIndex.org</strong>. Suspiciously low prices or
              scraped-off serials are red flags.
            </p>

            {/* SECTION: Local Regulations and Best Practices */}
            <h2 id="section-4">Local Regulations and Best Practices</h2>
            <p>
              Cycling in Vancouver is governed by provincial and municipal
              bylaws. Here are the key points:
            </p>
            <ul>
              <li>
                <strong>Helmet Law:</strong> BC law requires all riders (any
                age) to wear a certified helmet.
              </li>
              <li>
                <strong>Lights & Reflectors:</strong> A white front light and
                red rear light/reflector are mandatory after dusk.
              </li>
              <li>
                <strong>Bell or Horn:</strong> City bylaws demand an audible
                warning device on your bike.
              </li>
              <li>
                <strong>Bike Lanes & Etiquette:</strong> Use designated lanes,
                yield to pedestrians on shared paths, and avoid riding on
                sidewalks (unless posted otherwise).
              </li>
              <li>
                <strong>Lock it Up:</strong> Use a good U-lock and remove quick
                releases to deter theft.
              </li>
            </ul>

            {/* SECTION: Top Bike-Friendly Routes and Trails */}
            <h2 id="section-5">Top Bike-Friendly Routes and Trails</h2>
            <p>
              Once you have your bike, enjoy these scenic routes in and around
              Vancouver.
            </p>
            <ol>
              <li
                ref={(el) => setSpotRef("stanley-park", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Stanley Park Seawall Loop</strong>{" "}
                <Marker
                  coordY={49.29720}
                  coordX={-123.13000}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                – About 9 km of stunning waterfront with iconic views of the
                city and mountains.
              </li>
              <li
                ref={(el) => setSpotRef("science-world", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>False Creek & Kitsilano Seawall</strong>{" "}
                <Marker
                  coordY={49.27345}
                  coordX={-123.10350}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                – Loop around Science World, through Yaletown, and over to Kits
                Beach for a ~9–10 km scenic ride.
              </li>
              <li
                ref={(el) => setSpotRef("new-west-quay", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Central Valley Greenway</strong>{" "}
                <Marker
                  coordY={49.20197}
                  coordX={-122.91254}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                – 24 km from Vancouver to New Westminster, passing Burnaby Lake
                and quiet neighborhoods.
              </li>
              <li
                ref={(el) => setSpotRef("arbutus-start", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Arbutus Greenway</strong>{" "}
                <Marker
                  coordY={49.26800}
                  coordX={-123.13900}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />{" "}
                to{" "}
                <Marker
                  coordY={49.20950}
                  coordX={-123.14000}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                – A repurposed rail corridor (~9 km) from False Creek to Marpole
                along a smooth, wide path.
              </li>
              <li
                ref={(el) => setSpotRef("english-bay", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Seaside Greenway (English Bay to UBC)</strong>{" "}
                <Marker
                  coordY={49.28530}
                  coordX={-123.14285}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />{" "}
                – Up to 20 km one-way, with stops at{" "}
                <span
                  ref={(pt) => setSpotRef("jericho-beach", pt)}
                  style={{ scrollMarginTop: "80px" }}
                >
                  Jericho Beach
                  <Marker
                    coordY={49.27440}
                    coordX={-123.19334}
                    mobileMapRef={mobileMapRef}
                    mapRef={mapRef}
                  />
                </span>{" "}
                before tackling the hill to{" "}
                <span
                  ref={(pt) => setSpotRef("ubc-moa", pt)}
                  style={{ scrollMarginTop: "80px" }}
                >
                  UBC
                  <Marker
                    coordY={49.26980}
                    coordX={-123.25900}
                    mobileMapRef={mobileMapRef}
                    mapRef={mapRef}
                  />
                </span>
                .
              </li>
            </ol>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Do I need to wear a helmet?</h3>
              <p>
                Yes. Helmets are mandatory for all ages in BC, with fines for
                non-compliance. More importantly, a good helmet protects you
                from serious head injuries.
              </p>

              <h3>2. How do I avoid buying a stolen bike?</h3>
              <p>
                Always check the bike’s serial number on registries like Project
                529. If a seller won’t share the serial or seems suspiciously
                cheap, walk away.
              </p>

              <h3>3. What kind of bike is best for commuting?</h3>
              <p>
                Hybrid or city bikes typically offer a comfortable upright
                position, enough gears for Vancouver’s hills, and compatibility
                with fenders and racks. E-bikes are also becoming popular for
                tackling longer or hillier commutes.
              </p>
            </div>

            {/* SECTION: Additional Local Regulations (Already covered, so brief mention) */}
            <h2 id="local-regulations">Local Regulations</h2>
            <p>
              Beyond helmets and lights, remember that bicycles must follow the
              same traffic rules as cars: obey signs, signals, and yield to
              pedestrians. Riding on sidewalks is prohibited unless specifically
              allowed. Registering your bike on Project 529 is recommended but
              not legally required. Always keep safety in mind and respect other
              road or path users.
            </p>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Whether you’re investing in a shiny new ride or snagging a great
              second-hand deal, cycling in Vancouver is a rewarding experience.
              By following local rules, choosing the right bike for your needs,
              and exploring the city’s top routes, you’ll be well on your way to
              enjoying the freedom and benefits of biking in one of Canada’s
              most scenic urban landscapes. Now that you have the know-how,
              it’s time to gear up and ride!
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay ref={mobileMapRef} markers={locations} spotRefs={spotRefs} />

      <Footer />
    </>
  );
}